import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPaymentSheet } from '../utils/api';
import { prebidForJob, prebidTotals, MIN_MARGIN } from '../utils/prebid';

/* Cashflow — the office's paper payment sheet, filled in from the records.
   One row per job: Customer / Sales Amt · Cash/Finance · Dealer Fee · Deposit ·
   Collection ×3 · Financed Amt · Pre-paid Bills · Remaining Collection,
   then Already Received / Still Coming In — what's in hand vs. what's left to come.
   Print it (landscape) or download it for Excel.                            */

const TZ = 'America/Los_Angeles';
const todayCA = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
const money = n => (Number(n) ? '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—');
const moneyAlways = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Product colours — the same as the Job Tracker (first service on the job decides the colour)
const PRODUCT_COLORS = { roof: '#C6E0B4', coating: '#ADD8E6', paint: '#C9E4F2', windows: '#FF5050', insulation: '#CC66FF', gutters: '#FFE0B2', patio: '#FFF9C4', fencing: '#F8BBD0' };
const PRODUCT_LABELS = { roof: 'Roofing', coating: 'Coating', paint: 'Paint', windows: 'Windows', gutters: 'Gutters', insulation: 'Insulation', patio: 'Patio', fencing: 'Fencing' };
const serviceOf = r => r.svc1 || r.svc2 || r.svc3 || r.svc4 || '';
function productKey(service) { const s = String(service || '').toLowerCase(); return Object.keys(PRODUCT_COLORS).find(k => s.includes(k)) || 'other'; }
const productColor = service => PRODUCT_COLORS[productKey(service)] || '#CC66FF';   // anything else = purple, like the Job Tracker

const PRODUCT_ORDER = ['roof', 'coating', 'paint', 'windows', 'gutters', 'insulation', 'patio', 'fencing', 'other'];
const SUM_KEYS = ['sales_amt', 'dealer_fee', 'deposit', 'col1', 'col2', 'col3', 'financed', 'funded', 'prepaid_bills', 'remaining', 'received', 'coming', 'loan_pending'];
function sumRows(list) {
  const t = {};
  for (const k of SUM_KEYS) t[k] = Math.round(list.reduce((s, r) => s + (r[k] || 0), 0) * 100) / 100;
  return t;
}
// Jobs grouped by product (Roofing together, Coating together, …) in a fixed order
function groupByProduct(list) {
  const g = {};
  for (const r of list) { const k = productKey(serviceOf(r)); (g[k] = g[k] || []).push(r); }
  return PRODUCT_ORDER.filter(k => g[k]).map(k => ({ k, label: PRODUCT_LABELS[k] || 'Other', color: PRODUCT_COLORS[k] || '#CC66FF', rows: g[k], t: sumRows(g[k]), pb: prebidTotals(g[k]) }));
}

const pbPct = m => (m == null ? '—' : (m * 100).toFixed(1) + '%');

const COLS = [
  { k: 'deposit', label: 'Deposit' },
  { k: 'col1', label: 'Collection', sub: 'Removal' },
  { k: 'col2', label: 'Collection', sub: 'Start' },
  { k: 'col3', label: 'Collection', sub: 'Complete' },
];

const CSS = `
.ps-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:16px}
.ps-bar .form-input,.ps-bar .form-select{width:auto}
.ps-btn{border:1px solid #0D3B2E;background:#0D3B2E;color:#fff;border-radius:10px;padding:9px 16px;font-weight:700;font-size:13px;cursor:pointer}
.ps-btn.alt{background:#fff;color:#0D3B2E}
.ps-btn:hover{filter:brightness(1.08)}
.ps-wrap{background:#fff;border:1px solid #E3E9E5;border-radius:14px;overflow-x:auto}
.ps-title{display:none}
table.ps{width:100%;border-collapse:collapse;font-size:13.5px;min-width:1100px}
table.ps th{background:#F4F7F5;color:#33463E;font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;
  padding:10px 10px;border-bottom:2px solid #C9D6D0;border-right:1px solid #E1E8E4;text-align:right;vertical-align:bottom;line-height:1.25}
table.ps th:first-child,table.ps th.c{text-align:left}
table.ps th small{display:block;font-weight:600;text-transform:none;letter-spacing:0;color:#7a8a82;font-size:11px}
table.ps td{padding:9px 10px;border-bottom:1px solid #EAEFEC;border-right:1px solid #EEF2F0;text-align:right;vertical-align:top;white-space:nowrap}
table.ps td:first-child{text-align:left;white-space:normal;min-width:170px}
table.ps tr.row{cursor:pointer}
table.ps tr.row:hover{filter:brightness(.94)}
table.ps tr.row td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
.ps-legendrow{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 10px;font-size:12px;font-weight:700;color:#33463E}
.ps-legendrow span{display:inline-flex;align-items:center;gap:6px}
.ps-legendrow i{width:14px;height:14px;border-radius:4px;border:1px solid rgba(0,0,0,.15)}
.ps-prod{display:inline-block;font-size:10.5px;font-weight:800;color:#1d2522;background:rgba(255,255,255,.6);border-radius:5px;padding:0 6px;margin-top:3px}
.ps-name{font-weight:800;color:#0F241D}
.ps-sales{font-size:12.5px;color:#4A5D55;margin-top:2px;font-weight:700}
.ps-po{font-size:11px;color:#8a9892}
.ps-cf{text-align:left!important;font-size:12.5px;line-height:1.6}
.ps-box{display:inline-block;width:13px;height:13px;border:1.5px solid #4A5D55;border-radius:3px;vertical-align:-2px;margin-left:5px;text-align:center;font-size:11px;line-height:11px;font-weight:900;color:#0D3B2E}
.ps-due{display:block;font-size:11px;color:#B35C00;font-weight:700}
.ps-fee{color:#B23B2E}
.ps-rem{font-weight:800;color:#0D3B2E}
.ps-rem.owed{color:#B23B2E}
.ps-rem.over{color:#1565C0}
.ps-note{display:block;font-size:11px;color:#7a8a82;white-space:normal;max-width:140px;margin-left:auto}
table.ps tr.ps-gh td{padding:9px 12px;font-size:13px;color:#13211B;border-top:10px solid #fff;text-align:left;letter-spacing:.02em;-webkit-print-color-adjust:exact;print-color-adjust:exact}
table.ps tr.ps-gh:first-child td{border-top:0}
table.ps tr.ps-gh b{font-size:14px;text-transform:uppercase;letter-spacing:.06em}
table.ps tr.ps-gt td{font-weight:800;background:#fff;border-top:2px solid #13211B;border-bottom:3px double #13211B;box-shadow:inset 0 0 0 9999px color-mix(in srgb,var(--pc) 25%,transparent);-webkit-print-color-adjust:exact;print-color-adjust:exact}
table.ps tfoot td{font-weight:800;background:#F4F7F5;border-top:2px solid #C9D6D0}
.ps-empty{padding:24px;color:#7a8a82}
.ps-sum{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:14px}
@media(max-width:900px){.ps-sum{grid-template-columns:repeat(2,1fr)}}
.ps-s{background:#fff;border:1px solid #E3E9E5;border-radius:12px;padding:12px 16px}
.ps-s .k{font-size:11px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:#7a8a82}
.ps-s .v{font-size:22px;font-weight:800;color:#0F241D;margin-top:4px}
.ps-s .d{font-size:11.5px;color:#7a8a82;margin-top:2px}
.ps-s.in{background:#FFF8E6;border-color:#F3D27A}.ps-s.in .v{color:#8A5A00}
.ps-s.fee .v{color:#B23B2E}
.ps-s.come{background:linear-gradient(135deg,#0D3B2E,#1B5E40);border:none}
.ps-s.come .k,.ps-s.come .d{color:rgba(255,255,255,.78)}.ps-s.come .v{color:#F0C842}
table.ps th.ps-in-h{background:#FFF3D6;color:#8A5A00}
table.ps th.ps-come-h{background:#0D3B2E;color:#fff}table.ps th.ps-come-h small{color:rgba(255,255,255,.75)}
table.ps td.ps-in{background:#FFFBF0;color:#8A5A00;font-weight:700}
table.ps td.ps-come{background:#EEF6F1;color:#0D3B2E;font-weight:800;font-size:14.5px}
table.ps th.ps-pb-h{background:#EDE7F6;color:#4527A0}
.ps-mg{font-weight:800}.ps-mg.ok{color:#1B5E20}.ps-mg.bad{color:#B71C1C}.ps-mg.none,.ps-pf.none{color:#9aa6a0;font-weight:400}
.ps-pf{font-weight:700}.ps-pf.neg{color:#B71C1C}
.ps-legend{font-size:12px;color:#7a8a82;margin-top:10px;line-height:1.6}

@media print{
  @page{size:landscape;margin:8mm}
  body *{visibility:hidden!important}
  .ps-print,.ps-print *{visibility:visible!important}
  .ps-print{position:absolute;left:0;top:0;width:100%}
  .ps-title{display:flex!important;justify-content:space-between;align-items:baseline;margin:0 0 8px;font-family:Arial,sans-serif}
  .ps-title h1{font-size:16px;margin:0}.ps-title span{font-size:11px;color:#555}
  .ps-wrap{border:none;overflow:visible}
  table.ps{min-width:0;font-size:9.5px}
  table.ps th{font-size:8.5px;padding:5px 5px;background:#eee!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  table.ps td{padding:5px 5px}
  table.ps td:first-child{min-width:0}
  table.ps tr{page-break-inside:avoid}
  .ps-legend{font-size:9px}
  .ps-sum{grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:6px}
  .ps-s{padding:5px 8px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .ps-s .k{font-size:7.5px}.ps-s .v{font-size:13px}.ps-s .d{font-size:7.5px}
  table.ps th.ps-in-h,table.ps th.ps-come-h,table.ps td.ps-in,table.ps td.ps-come{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  table.ps td.ps-come{font-size:10.5px}
  table.ps th.ps-come-h{background:#0D3B2E!important;color:#fff!important}
  table.ps th.ps-in-h{background:#FFF3D6!important;color:#8A5A00!important}
  table.ps th.ps-pb-h{background:#EDE7F6!important;color:#4527A0!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  html,body,.page-body,.ps-print{background:#fff!important}
  table.ps th small{font-size:7.5px}
  .ps-name{font-size:10px}.ps-sales{font-size:9.5px}.ps-po{font-size:7.5px}
  .ps-due,.ps-note{font-size:7.5px}.ps-cf{font-size:9px!important;line-height:1.5}
  .ps-box{width:9px;height:9px;font-size:8px;line-height:8px;border-width:1px}
}
`;

function Amount({ paid, due }) {
  return (
    <>
      {money(paid)}
      {due > 0 && <span className="ps-due">due {moneyAlways(due)}</span>}
    </>
  );
}

export default function PaymentSheet() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('tracker');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    getPaymentSheet(status)
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) { setError(e.response?.data?.error || 'Could not load the sheet'); setData(null); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [status]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter(r => `${r.customer_name || ''} ${r.po_number || ''} ${r.city || ''}`.toLowerCase().includes(s));
  }, [data, q]);

  const pb = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.job_id] = prebidForJob(r);
    return m;
  }, [rows]);
  const pbTotal = useMemo(() => prebidTotals(rows), [rows]);

  const totals = useMemo(() => sumRows(rows), [rows]);
  const groups = useMemo(() => groupByProduct(rows), [rows]);

  const statusLabel = status === 'all' ? 'All jobs' : status === 'tracker' ? 'Active jobs' : `Sales status: ${status}`;

  function downloadCsv() {
    const head = ['Customer', 'PO #', 'City', 'Sales Amt', 'Cash', 'Finance', 'Lender(s)', 'Dealer Fee', 'Deposit',
      'Collection (Removal)', 'Collection (Start)', 'Collection (Complete)', 'Still Due (not paid)', 'Loan Amount', 'Funded (after dealer fee)',
      'Pre-paid Bills', 'Pre-paid For', 'Remaining Collection', 'Already Received', 'Still Coming In', 'Margin %', 'Profit', 'Sales Status'];
    const esc = v => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [head.map(esc).join(',')];
    const pbCells = t => [t.margin == null ? '' : (t.margin * 100).toFixed(1) + '%', t.count ? Math.round(t.profit * 100) / 100 : ''];
    for (const g of groups) for (const r of [...g.rows, null]) {
      if (!r) {   // subtotal under each product
        const t = g.t;
        lines.push([`${g.label} total (${g.rows.length} job${g.rows.length === 1 ? '' : 's'})`, '', '', t.sales_amt, '', '', '', t.dealer_fee, t.deposit, t.col1, t.col2, t.col3, '',
          t.financed, t.funded, t.prepaid_bills, '', t.remaining, t.received, t.coming, ...pbCells(g.pb), ''].map(esc).join(','));
        lines.push('');
        continue;
      }
      lines.push([r.customer_name, r.po_number, r.city, r.sales_amt, r.cash ? 'Yes' : '', r.finance ? 'Yes' : '', r.lenders.join(' / '),
        r.dealer_fee, r.deposit, r.col1, r.col2, r.col3, r.deposit_due + r.col1_due + r.col2_due + r.col3_due,
        r.financed, r.funded, r.prepaid_bills, r.prepaid_notes, r.remaining, r.received, r.coming,
        pb[r.job_id] ? (pb[r.job_id].margin * 100).toFixed(1) + '%' : '', pb[r.job_id] ? Math.round(pb[r.job_id].profit * 100) / 100 : '', r.sales_status].map(esc).join(','));
    }
    lines.push(['TOTAL', '', '', totals.sales_amt, '', '', '', totals.dealer_fee, totals.deposit, totals.col1, totals.col2, totals.col3, '',
      totals.financed, totals.funded, totals.prepaid_bills, '', totals.remaining, totals.received, totals.coming,
      pbTotal.margin == null ? '' : (pbTotal.margin * 100).toFixed(1) + '%', pbTotal.count ? Math.round(pbTotal.profit * 100) / 100 : '', ''].map(esc).join(','));
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cashflow-${status === 'tracker' ? 'active' : status === 'all' ? 'all' : status.toLowerCase().replace(/\s+/g, '-')}-${todayCA()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header">
        <h2>Cashflow</h2>
        <div style={{ fontSize: 13, color: '#888' }}>Every job's money at a glance — filled in automatically from the records. Print it or download it for Excel.</div>
      </div>
      <div className="page-body">
        <div className="ps-bar">
          <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="tracker">Active jobs (as in Job Tracker)</option>
            {(data?.statuses || []).map(s => <option key={s} value={s}>Sales status: {s}</option>)}
            <option value="all">All jobs</option>
          </select>
          <input className="form-input" placeholder="Search customer, PO or city" value={q} onChange={e => setQ(e.target.value)} style={{ width: 240 }} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="ps-btn alt" onClick={downloadCsv} disabled={!rows.length}>⬇ Download (Excel)</button>
            <button className="ps-btn" onClick={() => window.print()} disabled={!rows.length}>🖨 Print / Save as PDF</button>
          </div>
        </div>

        {error && <div style={{ background: '#FDECEA', border: '1px solid #F5B7B1', color: '#8E1B10', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>{error}</div>}
        {loading && !data && <div className="loader"><div className="spinner" /></div>}

        {data && (
          <div className="ps-print">
            <div className="ps-title"><h1>CPR Home Solutions — Cashflow · {statusLabel}</h1><span>Printed {new Date().toLocaleDateString('en-US', { timeZone: TZ, month: 'long', day: 'numeric', year: 'numeric' })} · {rows.length} job{rows.length === 1 ? '' : 's'}</span></div>
            {rows.length > 0 && (
              <div className="ps-sum">
                <div className="ps-s"><div className="k">Total sold</div><div className="v">{moneyAlways(totals.sales_amt)}</div><div className="d">{rows.length} job{rows.length === 1 ? '' : 's'}, change order total or contract</div></div>
                <div className="ps-s in"><div className="k">Already received</div><div className="v">{moneyAlways(totals.received)}</div><div className="d">In our hands already — likely spent</div></div>
                <div className="ps-s fee"><div className="k">Dealer fees</div><div className="v">− {moneyAlways(totals.dealer_fee)}</div><div className="d">Kept by the lenders — never reaches us</div></div>
                <div className="ps-s come"><div className="k">Still coming in</div><div className="v">{moneyAlways(totals.coming)}</div><div className="d">The only money left to come from these jobs</div></div>
              </div>
            )}
            {rows.length > 0 && (
              <div className="ps-legendrow">
                {groups.map(g => (
                  <span key={g.k}><i style={{ background: g.color }} />{g.label} · {g.rows.length}</span>
                ))}
              </div>
            )}
            <div className="ps-wrap">
              {rows.length === 0 ? <div className="ps-empty">No {statusLabel.toLowerCase()}{q ? ' match your search' : ''}.</div> : (
                <table className="ps">
                  <thead>
                    <tr>
                      <th>Customer<small>Sales Amt</small></th>
                      <th className="c">Cash<small>Finance</small></th>
                      <th>Dealer<br />Fee</th>
                      {COLS.map(c => <th key={c.k}>{c.label}{c.sub && <small>{c.sub}</small>}</th>)}
                      <th>Financed<br />Amt<small>after dealer fee</small></th>
                      <th>Pre-paid<br />Bills</th>
                      <th>Remaining<br />Collection</th>
                      <th className="ps-in-h">Already<br />Received<small>in our hands</small></th>
                      <th className="ps-come-h">Still<br />Coming In<small>to us</small></th>
                      <th className="ps-pb-h">Margin<br />%<small>from Pre-Bid</small></th>
                      <th className="ps-pb-h">Profit<small>left over</small></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map(g => [
                      <tr key={`h-${g.k}`} className="ps-gh"><td colSpan={14} style={{ background: g.color }}><b>{g.label}</b> · {g.rows.length} job{g.rows.length === 1 ? '' : 's'}</td></tr>,
                      ...g.rows.map(r => (
                      <tr key={r.job_id} className="row" title="Open this record" style={{ background: productColor(serviceOf(r)) }}
                        onClick={() => r.po_number && navigate(`/update?po=${encodeURIComponent(r.po_number)}`)}>
                        <td>
                          <div className="ps-name">{r.customer_name || '—'}</div>
                          {serviceOf(r) && <span className="ps-prod">{serviceOf(r)}</span>}
                          <div className="ps-sales">{moneyAlways(r.sales_amt)}</div>
                          <div className="ps-po">{r.po_number}{r.city ? ` · ${r.city}` : ''}</div>
                        </td>
                        <td className="ps-cf">
                          C<span className="ps-box">{r.cash ? '✓' : ''}</span><br />
                          F<span className="ps-box">{r.finance ? '✓' : ''}</span>
                        </td>
                        <td className="ps-fee">{r.dealer_fee ? moneyAlways(r.dealer_fee) : (r.finance ? '$0' : '—')}</td>
                        {COLS.map(c => <td key={c.k}><Amount paid={r[c.k]} due={r[c.k + '_due']} /></td>)}
                        <td>{r.finance ? moneyAlways(r.funded) : '—'}{r.finance && <span className="ps-note">{r.dealer_fee ? `${moneyAlways(r.financed)} loan − ${moneyAlways(r.dealer_fee)} fee` : `${moneyAlways(r.financed)} loan`}{r.lenders.length > 0 ? ` · ${r.lenders.join(' / ')}` : ''}</span>}{r.loan_pending > 0 && <span className="ps-due">{moneyAlways(r.loan_pending)} not funded yet</span>}</td>
                        <td>{money(r.prepaid_bills)}{r.prepaid_notes && <span className="ps-note">{r.prepaid_notes}</span>}</td>
                        <td className={'ps-rem ' + (r.remaining > 0 ? 'owed' : r.remaining < 0 ? 'over' : '')}>
                          {r.remaining < 0 ? `${moneyAlways(-r.remaining)} over` : moneyAlways(r.remaining)}
                        </td>
                        <td className="ps-in">{moneyAlways(r.received)}{r.sales_amt > 0 && <span className="ps-note">{Math.round(r.received / r.sales_amt * 100)}% of the job</span>}</td>
                        <td className="ps-come">{moneyAlways(r.coming)}</td>
                        {(() => { const p = pb[r.job_id]; return p ? <>
                          <td className={'ps-mg ' + (p.ok ? 'ok' : 'bad')}>{pbPct(p.margin)}</td>
                          <td className={'ps-pf' + (p.profit < 0 ? ' neg' : '')}>{moneyAlways(Math.round(p.profit))}</td>
                        </> : <><td className="ps-mg none">—</td><td className="ps-pf none">—</td></>; })()}
                      </tr>
                    ))
                      ,
                      <tr key={`t-${g.k}`} className="ps-gt" style={{ '--pc': g.color }}>
                        <td>{g.label} total · {g.rows.length} job{g.rows.length === 1 ? '' : 's'}<div className="ps-sales">{moneyAlways(g.t.sales_amt)}</div></td>
                        <td></td>
                        <td className="ps-fee">{moneyAlways(g.t.dealer_fee)}</td>
                        {COLS.map(c => <td key={c.k}>{moneyAlways(g.t[c.k])}</td>)}
                        <td>{moneyAlways(g.t.funded)}<span className="ps-note">{moneyAlways(g.t.financed)} in loans</span></td>
                        <td>{moneyAlways(g.t.prepaid_bills)}</td>
                        <td className="ps-rem">{moneyAlways(g.t.remaining)}</td>
                        <td className="ps-in">{moneyAlways(g.t.received)}</td>
                        <td className="ps-come">{moneyAlways(g.t.coming)}</td>
                        <td className={'ps-mg ' + (g.pb.margin == null ? 'none' : g.pb.margin >= MIN_MARGIN ? 'ok' : 'bad')}>{pbPct(g.pb.margin)}</td>
                        <td className={'ps-pf' + (!g.pb.count ? ' none' : g.pb.profit < 0 ? ' neg' : '')}>{g.pb.count ? moneyAlways(Math.round(g.pb.profit)) : '—'}</td>
                      </tr>,
                    ])}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total · {rows.length} job{rows.length === 1 ? '' : 's'}<div className="ps-sales">{moneyAlways(totals.sales_amt)}</div></td>
                      <td></td>
                      <td className="ps-fee">{moneyAlways(totals.dealer_fee)}</td>
                      {COLS.map(c => <td key={c.k}>{moneyAlways(totals[c.k])}</td>)}
                      <td>{moneyAlways(totals.funded)}<span className="ps-note">{moneyAlways(totals.financed)} in loans</span></td>
                      <td>{moneyAlways(totals.prepaid_bills)}</td>
                      <td className="ps-rem">{moneyAlways(totals.remaining)}</td>
                      <td className="ps-in">{moneyAlways(totals.received)}</td>
                      <td className="ps-come">{moneyAlways(totals.coming)}</td>
                      <td className={'ps-mg ' + (pbTotal.margin == null ? 'none' : pbTotal.margin >= MIN_MARGIN ? 'ok' : 'bad')}>{pbPct(pbTotal.margin)}{pbTotal.count > 0 && <span className="ps-note">{pbTotal.count} job{pbTotal.count === 1 ? '' : 's'} with Pre-Bid</span>}</td>
                      <td className={'ps-pf' + (!pbTotal.count ? ' none' : pbTotal.profit < 0 ? ' neg' : '')}>{pbTotal.count ? moneyAlways(Math.round(pbTotal.profit)) : '—'}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
            <div className="ps-legend">
              Jobs are grouped by product (the first service on the job), with a total under each product and the grand total at the bottom. Sales Amt = the Change Order Amount (the job's new total) when there is one, otherwise the Contract Amount. Amounts are payments marked Collected; <span style={{ color: '#B35C00', fontWeight: 700 }}>due</span> = marked Due, not yet paid.
              Financed Amt = what we receive: loan amount minus the dealer fee (every loan that isn't No Go or Cancelled). Remaining = Sales Amt − Deposit − Collections − full loan amount — the dealer fee is our cost, the customer doesn't owe it. <b>Already Received</b> = cash collected + loans already funded (after fee). <b>Still Coming In</b> = Remaining + loans approved but not funded yet (after fee). Received + Still Coming + Dealer Fee = Sales Amt.{' '}
              <b>Margin % / Profit</b> come from each job's Pre-Bid tab: sale price − labor &amp; material − 2.7% marketing − rep commission. Green = {Math.round(MIN_MARGIN * 100)}% or more, red = below. The total margin is total profit ÷ total sale price of the jobs that have Pre-Bid info.
            </div>
          </div>
        )}
      </div>
    </>
  );
}
